import React, { useState } from 'react';
import { Modal } from '../Modal';
import { Button } from '../Button';
import { CATEGORY_CONFIGS, StockCategoryFilter } from '../../../types/stockCategories';
import { CATEGORIES } from '../../../config/categoryConfig';
import { usePremium } from '../../../hooks/usePremium';
import { useCategoryAccess } from '../../../contexts/AccessControlContext';
import { AlertCircle, CheckCircle, Copy, Loader2 } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { CreateLeaderboardRequest, CreateLeaderboardResponse } from '../../../types/api';

interface CreateLeaderboardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (groupId: string, inviteCode: string) => void;
  createLeaderboard: (request: CreateLeaderboardRequest) => Promise<CreateLeaderboardResponse>;
  isProcessing: boolean;
  userGroupCount: number;
}

/**
 * Helper function to get mobile-friendly category labels
 */
const getMobileLabel = (category: StockCategoryFilter): string => {
  const mobileLabels: Partial<Record<StockCategoryFilter, string>> = {
    'popular': 'Popular',
    'tech': 'Tech',
    'pharma': 'Pharma',
    'wallstreetbets': 'WSB',
    'foreign': 'Foreign'
  };
  return mobileLabels[category] || CATEGORY_CONFIGS[category]?.label || category;
};

/**
 * Category button component with access control
 */
const CategoryButton: React.FC<{
  category: StockCategoryFilter;
  config: typeof CATEGORY_CONFIGS[StockCategoryFilter];
  isSelected: boolean;
  onToggle: () => void;
  disabled: boolean;
}> = ({ category, config, isSelected, onToggle, disabled }) => {
  const accessCheck = useCategoryAccess(category);

  const handleClick = () => {
    // Only allow interaction if not locked and not disabled
    if (!accessCheck.isLocked && !disabled) {
      onToggle();
    }
    // Locked categories do nothing on click
  };

  // Determine if button should show locked state
  const isLocked = accessCheck.isLocked && !disabled;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || isLocked}
      className={cn(
        'p-3 rounded-lg border-2 transition-all',
        'flex items-center gap-2 text-left justify-center',
        // Locked state styling - grayed out, non-clickable
        isLocked
          ? 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 opacity-50 cursor-not-allowed'
          : isSelected
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 hover:shadow-md'
          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-md',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
      title={isLocked ? `🔒 ${getMobileLabel(category)} - Premium Required` : getMobileLabel(category)}
      aria-label={isLocked ? `Locked: ${getMobileLabel(category)}` : getMobileLabel(category)}
    >
      <span className="text-2xl">{config.icon}</span>
      <span className="text-sm font-medium">{getMobileLabel(category)}</span>
      {/* No icons - blue outline indicates selection */}
    </button>
  );
};

export const CreateLeaderboardModal: React.FC<CreateLeaderboardModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  createLeaderboard,
  isProcessing,
  userGroupCount
}) => {
  const { isPremium, loading: premiumLoading } = usePremium();

  const [formData, setFormData] = useState({
    name: '',
    description: '', // Keep for API compatibility but don't show in UI
    tracked_categories: ['popular', 'tech', 'wallstreetbets'] as string[],
    max_members: 100
  });

  const [error, setError] = useState<string | null>(null);
  const [successData, setSuccessData] = useState<{ invite_code: string; invite_link: string } | null>(null);
  const [copied, setCopied] = useState(false);

  // Check if user can create more leaderboards (only when not loading)
  const canCreate = !premiumLoading && (isPremium || userGroupCount < 1);
  const remainingSlots = isPremium ? '∞' : Math.max(0, 1 - userGroupCount).toString();

  const handleCategoryToggle = (category: StockCategoryFilter) => {
    setFormData(prev => ({
      ...prev,
      tracked_categories: prev.tracked_categories.includes(category)
        ? prev.tracked_categories.filter(c => c !== category)
        : [...prev.tracked_categories, category]
    }));
  };

  const handleSubmit = async () => {
    if (isProcessing) {
      return;
    }

    setError(null);

    // Validation
    if (!formData.name.trim()) {
      setError('Please enter a leaderboard name');
      return;
    }

    if (formData.tracked_categories.length === 0) {
      setError('Please select at least one category');
      return;
    }

    if (!canCreate) {
      setError('Free users are limited to 1 private leaderboard. Upgrade to Premium for unlimited access.');
      return;
    }

    // Create leaderboard
    const result = await createLeaderboard(formData);

    if (result.success && result.invite_code && result.invite_link) {
      setSuccessData({
        invite_code: result.invite_code,
        invite_link: result.invite_link
      });
      onSuccess?.(result.group_id || '', result.invite_code);
    } else {
      setError(result.error || 'Failed to create leaderboard');
    }
  };

  const handleCopyCode = () => {
    if (successData?.invite_code) {
      if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
        console.warn('Clipboard API not available in this environment');
        return;
      }

      navigator.clipboard.writeText(successData.invite_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopyLink = () => {
    if (successData?.invite_link) {
      if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
        console.warn('Clipboard API not available in this environment');
        return;
      }

      navigator.clipboard.writeText(successData.invite_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      tracked_categories: ['popular', 'tech', 'wallstreetbets'],
      max_members: 100
    });
    setError(null);
    setSuccessData(null);
    setCopied(false);
    onClose();
  };

  // Success view
  if (successData) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Leaderboard Created!"
        className="max-w-md"
      >
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
          </div>

          <p className="text-center text-gray-700 dark:text-gray-300">
            Your private leaderboard has been created successfully!
          </p>

          {/* Invite Code */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Invite Code
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={successData.invite_code}
                readOnly
                className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-center font-mono text-lg"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={handleCopyCode}
                className="flex-shrink-0"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Invite Link */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Shareable Link
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={successData.invite_link}
                readOnly
                className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-sm truncate"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={handleCopyLink}
                className="flex-shrink-0"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {copied && (
            <div className="text-center text-sm text-green-600 dark:text-green-400">
              ✓ Copied to clipboard!
            </div>
          )}

          <Button onClick={handleClose} className="w-full">
            Done
          </Button>
        </div>
      </Modal>
    );
  }

  // Create form view
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create Private Leaderboard"
      description="Create a private leaderboard to compete with your friends"
      className="max-w-2xl"
    >
      <div className="p-6 space-y-4">
        {/* Premium Loading State */}
        {premiumLoading && (
          <div className="p-4 bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-800 rounded-lg flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-gray-600 dark:text-gray-400" />
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Loading subscription status...
            </div>
          </div>
        )}

        {/* Access Gate Warning - Only show after loading is complete */}
        {!premiumLoading && !canCreate && (
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800 dark:text-amber-200">
              <p className="font-medium">Free Tier Limit Reached</p>
              <p className="mt-1">
                Free users are limited to 1 private leaderboard. You currently have {userGroupCount} leaderboard{userGroupCount === 1 ? '' : 's'}.
                Upgrade to Premium for unlimited access.
              </p>
            </div>
          </div>
        )}

        {/* Leaderboard Name */}
        <div className="space-y-2">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Leaderboard Name <span className="text-red-500">*</span>
          </label>
          <input
            id="name"
            type="text"
            maxLength={100}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="My Awesome Leaderboard"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
            disabled={premiumLoading || !canCreate}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {formData.name.length}/100 characters
          </p>
        </div>

        {/* Categories */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Tracked Categories <span className="text-red-500">*</span>
          </label>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Select which categories to include in this leaderboard
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {CATEGORIES.map((cat) => {
              const category = cat.slug as StockCategoryFilter;
              const config = CATEGORY_CONFIGS[category];
              const isSelected = formData.tracked_categories.includes(category);
              return (
                <CategoryButton
                  key={category}
                  category={category}
                  config={config}
                  isSelected={isSelected}
                  onToggle={() => handleCategoryToggle(category)}
                  disabled={premiumLoading || !canCreate}
                />
              );
            })}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <Button
            variant="outline"
            onClick={handleClose}
            className="flex-1"
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="flex-1"
            disabled={isProcessing || premiumLoading || !canCreate}
          >
            {isProcessing ? 'Creating...' : 'Create Leaderboard'}
          </Button>
        </div>

        {/* Remaining Slots Info */}
        <div className="text-center text-sm text-gray-500 dark:text-gray-400">
          {premiumLoading ? (
            <span>Loading subscription status...</span>
          ) : isPremium ? (
            <span className="text-blue-600 dark:text-blue-400 font-medium">
              ✨ Premium: Unlimited leaderboards
            </span>
          ) : (
            <span>
              Remaining slots: <span className="font-medium">{remainingSlots}/1</span>
            </span>
          )}
        </div>
      </div>
    </Modal>
  );
};
